import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { Amplify } from 'aws-amplify';
import { signOut, signInWithRedirect, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { AnyMongoAbility } from '@casl/ability';
import { Hub } from 'aws-amplify/utils';
import { AnyPermission, createAbilityFromPermissions } from '@nohara/oidc-core';

interface AuthContextType {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
  userId: string | undefined;
  error: Error | undefined;
  ability: AnyMongoAbility | undefined;
  permissions: AnyPermission[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useOidc = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export interface OidcConfig {
  apiEndpoint: string;
  oauthEndpoint: string;
  userPoolId: string;
  userPoolWebClientId: string;
  oidcCallbackPath: string;
  signOutPath: string;
}

interface AuthProviderProps {
  children: ReactNode;
  config: OidcConfig;
}

export const OidcProvider = ({ children, config }: AuthProviderProps) => {
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [permissions, setPermissions] = useState<AnyPermission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  const checkAuthState = useCallback(async () => {
    try {
      setIsLoading(true);

      const authUser = await getCurrentUser();
      const session = await fetchAuthSession();

      if (authUser && session) {
        setUserId(authUser.userId);
        setPermissions(
          session.tokens?.accessToken?.payload?.['custom:permissions'] as unknown as AnyPermission[]
        );
      }
    } catch {
      // User is not authenticated
      setUserId(undefined);
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);

    try {
      await signInWithRedirect();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to login'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);

    try {
      await signOut({ global: true });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to logout'));
      throw err;
    }
  }, []);

  // Listen for auth events, including session expiration
  useEffect(() => {
    const hubListener = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'tokenRefresh':
          // Handle session expiration by setting user to null and clearing permissions
          checkAuthState();
          break;
        case 'tokenRefresh_failure':
          // Handle token refresh failure, which can indicate session expiration
          checkAuthState();
          break;
        case 'signedOut':
          // Ensure user state is properly reset on sign out
          setUserId(undefined);
          setPermissions([]);
          break;
        default:
          break;
      }
    });

    // Clean up the listener when component unmounts
    return () => {
      hubListener();
    };
  }, [checkAuthState]);

  useEffect(() => {
    if (!config) {
      throw new Error('OidcConfig is required');
    }

    const currentOrigin = window.location.origin;

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.userPoolId,
          userPoolClientId: config.userPoolWebClientId,
          loginWith: {
            oauth: {
              domain: config.oauthEndpoint,
              redirectSignIn: [`${currentOrigin}${config.oidcCallbackPath}`],
              redirectSignOut: [`${currentOrigin}${config.signOutPath}`],
              scopes: ['email', 'openid', 'profile'],
              responseType: 'code',
            },
          },
        },
      },
    });

    // Check if user is already signed in
    checkAuthState();
  }, [checkAuthState, config]);

  const ability = useMemo(() => {
    if (!permissions) {
      return undefined;
    }
    return createAbilityFromPermissions(permissions);
  }, [permissions]);

  const value = {
    userId,
    isAuthenticated: !!userId,
    permissions,
    ability,
    login,
    logout,
    isLoading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
